import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container, Paper, Typography, TextField, Button, Grid, Card, CardContent,
  Alert, CircularProgress, Stepper, Step, StepLabel, FormControl, InputLabel,
  Select, MenuItem, Box, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, Divider, Chip, Avatar
} from '@mui/material';
import { CameraAlt, Phone, CheckCircle, Send, Download, Visibility, Error, Description, ArrowBack, Business, Security, VerifiedUser, CalendarToday, Cancel } from '@mui/icons-material';
import { firmaApi } from '../services/api.ts';
import VerificacionEnviada from './VerificacionEnviada.tsx';

interface LinkData {
  id: string;
  titulo: string;
  descripcion: string;
  firmante: {
    nombre: string;
    email: string;
    telefono: string;
    tipoDocumento: string;
    numeroDocumento: string;
    esEmpresa: boolean;
  };
  fechaCreacion: string;
  fechaExpiracion: string;
  estado: string;
  firmado: boolean;
  fechaFirma: string | null;
}

interface VerificacionData {
  nombre: string;
  email: string;
  telefono: string;
  tipoDocumento: string;
  numeroDocumento: string;
  esEmpresa: boolean;
  cuit?: string;
  documentoPersoneria?: string; // Base64 del documento
}

const ClienteFirma: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [linkData, setLinkData] = useState<LinkData | null>(null);
  const [verificacionData, setVerificacionData] = useState<VerificacionData>({
    nombre: '',
    email: '',
    telefono: '',
    tipoDocumento: 'DNI',
    numeroDocumento: '',
    esEmpresa: false,
    cuit: '',
    documentoPersoneria: ''
  });
  const [documentoFile, setDocumentoFile] = useState<File | null>(null);
  const [personeriaFile, setPersoneriaFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [showVerificacionEnviada, setShowVerificacionEnviada] = useState(false);
  const [verificacionResult, setVerificacionResult] = useState<any>(null);
  const [clienteValidado, setClienteValidado] = useState(false);
  const [validandoCliente, setValidandoCliente] = useState(false);
  const [errorValidacion, setErrorValidacion] = useState<string>('');
  const [documentosSubidos, setDocumentosSubidos] = useState(false);
  const [subiendoDocumentos, setSubiendoDocumentos] = useState(false);
  const [subiendoSelfie, setSubiendoSelfie] = useState(false);
  const [enviandoOTP, setEnviandoOTP] = useState(false);
  const [verificandoOTP, setVerificandoOTP] = useState(false);
  const [selfieCapturada, setSelfieCapturada] = useState(false);
  const [documentoYaFirmado, setDocumentoYaFirmado] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const photoRef = useRef<HTMLCanvasElement>(null);

  const steps = [
    'Confirmar Datos',
    'Subir DNI',
    'Subir Selfie',
    'Verificación SMS',
    'Verificación Completada'
  ];

  useEffect(() => {
    cargarLinkData();
  }, []);

  // Verificar si la identidad ya está validada
  useEffect(() => {
    console.log('🔍 ClienteFirma: useEffect ejecutándose, linkData:', !!linkData);
    console.log('🔍 ClienteFirma: linkData.estado:', linkData?.estado);
    if (linkData && linkData.estado) {
      console.log('🔍 ClienteFirma: Llamando a verificarEstadoIdentidad...');
      verificarEstadoIdentidad();
    } else {
      console.log('🔍 ClienteFirma: No se llama verificarEstadoIdentidad porque no hay linkData o estado');
    }
  }, [linkData]);

  const verificarEstadoIdentidad = async () => {
    try {
      console.log('🔍 ClienteFirma: Verificando estado de identidad...');
      console.log('🔍 ClienteFirma: linkData completo:', linkData);
      
      // Usar la información que ya tenemos de linkData en lugar de hacer otra llamada
      if (linkData && linkData.estado) {
        const estado = linkData.estado;
        console.log('🔍 ClienteFirma: Estado de verificación desde linkData:', estado);
        
        if (estado === 'VERIFICADO' || estado === 'VALIDADO' || estado === 'FIRMANDO' || estado === 'FIRMADO' || estado === 'COMPLETADO') {
          console.log('✅ ClienteFirma: Identidad ya verificada, yendo al paso 4');
          // La identidad ya está validada, ir al paso final
          setActiveStep(4);
          
          if (estado === 'FIRMANDO') {
            setSuccess('Tu identidad ya ha sido verificada y el documento está listo para firma. Adobe Sign te enviará un email para completar la firma.');
          } else if (estado === 'FIRMADO' || estado === 'COMPLETADO') {
            setSuccess('Tu identidad ya ha sido verificada y el documento ya ha sido firmado.');
          } else {
            setSuccess('Tu identidad ya ha sido verificada anteriormente. Esperando que otros firmantes completen su verificación.');
          }
        } else {
          console.log('ℹ️ ClienteFirma: Estado no es VERIFICADO/VALIDADO/FIRMANDO/FIRMADO/COMPLETADO:', estado);
        }
      } else {
        console.log('ℹ️ ClienteFirma: No hay linkData disponible para verificar estado');
        console.log('ℹ️ ClienteFirma: linkData:', linkData);
      }
    } catch (error) {
      console.log('ℹ️ ClienteFirma: Error verificando estado:', error);
    }
  };

  // Limpiar cámara cuando el componente se desmonte
  useEffect(() => {
    return () => {
      closeCamera();
    };
  }, []);

  const cargarLinkData = async () => {
    try {
      setLoading(true);
      const response = await firmaApi.obtenerLink(token!);
      
      // Verificar si la respuesta indica un error de validación
      if (response.data && !response.data.success && response.data.error === 'VALIDATION_ERROR') {
        setError(response.data.message || 'Error al cargar la información del link');
        return;
      }
      
      console.log('📄 ClienteFirma: Datos del link recibidos:', response.data);
      
      // ✅ NUEVO: Log detallado de los datos recibidos
      if (response.data && response.data.data) {
        console.log('📄 ClienteFirma: Detalles del documento:', {
          titulo: response.data.data.titulo,
          descripcion: response.data.data.descripcion,
          fechaCreacion: response.data.data.documento?.created_at,
          fechaExpiracion: response.data.data.documento?.fecha_expiracion,
          estado: response.data.data.estado,
          firmante: response.data.data.firmante,
          documento: response.data.data.documento
        });
      } else {
        console.error('❌ ClienteFirma: response.data o response.data.data es null/undefined');
        console.error('❌ ClienteFirma: response.data:', response.data);
      }
      
      // ✅ NUEVO: Log específico del estado
      if (response.data && response.data.data) {
        console.log('🔍 ClienteFirma: Estado recibido del backend:', response.data.data.estado);
        console.log('🔍 ClienteFirma: ¿Es FIRMADO?', response.data.data.estado === 'FIRMADO');
      }
      
      console.log('🔍 ClienteFirma: Estableciendo linkData...');
      setLinkData(response.data.data);
      console.log('🔍 ClienteFirma: linkData establecido, esto debería trigger el useEffect');
      
      // Verificar si el documento ya fue firmado
      if (response.data.data && response.data.data.firmado) {
        setDocumentoYaFirmado(true);
        return;
      }
      
      // Pre-llenar datos del firmante
      if (response.data.data && response.data.data.firmante) {
        setVerificacionData({
          nombre: response.data.data.firmante.nombre,
          email: response.data.data.firmante.email,
          telefono: response.data.data.firmante.telefono,
          tipoDocumento: response.data.data.firmante.tipoDocumento,
          numeroDocumento: response.data.data.firmante.numeroDocumento,
          esEmpresa: response.data.data.firmante.esEmpresa
        });
      }
    } catch (err: any) {
      // Si es un error de validación del backend
      if (err.response?.data?.error === 'VALIDATION_ERROR') {
        setError(err.response.data.message || 'Error al cargar la información del link');
      } else {
        setError('Error al cargar la información del link: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const validarCliente = async () => {
    try {
      setValidandoCliente(true);
      setErrorValidacion('');
      
      // Validar que todos los campos estén completos
      if (!verificacionData.nombre || !verificacionData.email || !verificacionData.telefono || 
          !verificacionData.numeroDocumento) {
        setErrorValidacion('Por favor completa todos los campos obligatorios');
        return false;
      }

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(verificacionData.email)) {
        setErrorValidacion('El formato del email no es válido');
        return false;
      }

      // Validar email con el backend
      try {
        const response = await firmaApi.validarEmailFirmante(token!, verificacionData.email);
        console.log('✅ Respuesta de validación:', response.data);
        
        // Verificar si la validación fue exitosa
        if (!response.data.success) {
          console.log('❌ Validación fallida:', response.data);
          setErrorValidacion(response.data.message || 'El email no corresponde al firmante autorizado para este documento');
          return false;
        }
        
        // Si la validación es exitosa, actualizar los datos del firmante con los datos del backend
        if (response.data.data) {
          console.log('✅ Validación exitosa, actualizando datos del firmante:', response.data.data);
          setVerificacionData(prev => ({
            ...prev,
            nombre: response.data.data.firmante,
            email: response.data.data.email,
            telefono: response.data.data.telefono,
            tipoDocumento: response.data.data.tipoDocumento,
            numeroDocumento: response.data.data.numeroDocumento,
            esEmpresa: response.data.data.esEmpresa
          }));
        }
      } catch (err: any) {
        setErrorValidacion(err.response?.data?.message || 'Error al validar el email');
        return false;
      }

      // La validación del backend ya es suficiente, no necesitamos validación adicional

      console.log('✅ Cliente validado exitosamente, continuando al siguiente paso');
      setClienteValidado(true);
      return true;
    } catch (error: any) {
      setErrorValidacion('Error al validar los datos del cliente');
      return false;
    } finally {
      setValidandoCliente(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = event.target.files?.[0];
    if (file) {
      switch (type) {
        case 'documento':
          setDocumentoFile(file);
          break;
        case 'personeria':
          setPersoneriaFile(file);
          break;
        case 'selfie':
          setSelfieFile(file);
          setSelfieCapturada(true);
          console.log('📸 Selfie subida:', file.name, 'Tamaño:', file.size);
          break;
      }
      setError('');
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      const validado = await validarCliente();
      if (!validado) {
        return;
      }
    }
    if (activeStep === 1) {
      if (!documentoFile) {
        setError('Por favor adjunta tu documento de identidad');
        return;
      }
      const documentosSubidos = await subirDocumentos();
      if (!documentosSubidos) {
        return;
      }
    }
    if (activeStep === 2) {
      if (!selfieFile) {
        setError('Por favor captura tu selfie');
        return;
      }
      // Subir la selfie en este paso
      const selfieSubida = await subirSelfie();
      if (!selfieSubida) {
        return;
      }
    }
    if (activeStep === 3) {
      if (!otpVerified) {
        setError('Por favor verifica el código OTP');
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const subirSelfie = async () => {
    try {
      setSubiendoSelfie(true);
      setError('');

      const formData = new FormData();
      if (selfieFile) {
        formData.append('selfie', selfieFile);
      }
      
      // Agregar datos de verificación incluyendo el token
      const verificacionDataConToken = {
        ...verificacionData,
        token: token // Agregar el token del URL
      };
      formData.append('verificacionData', JSON.stringify(verificacionDataConToken));

      const response = await firmaApi.subirDocumentosVerificacion(formData);
      
      // Manejar diferentes estados de respuesta SOLO en el paso de selfie
      if (response.data.success) {
        const { estado, verificacionId } = response.data.data;
        
        if (estado === 'VERIFICADO') {
          // Verificación completada automáticamente
          setVerificacionResult(response.data.data);
          setShowVerificacionEnviada(true);
          setSuccess('Verificación completada automáticamente. Puedes continuar con la firma.');
          return true;
        } else if (estado === 'PENDIENTE_APROBACION') {
          // Verificación pendiente
          setVerificacionResult(response.data.data);
          setShowVerificacionEnviada(true);
          setSuccess('Documentos subidos exitosamente. Verificación enviada para revisión.');
          return true;
        } else if (estado === 'PENDIENTE_SMS') {
          // Continuar con el flujo normal (SMS)
          setSuccess('Selfie subida exitosamente. Continúa con la verificación SMS.');
          return true;
        } else {
          // Otro estado, continuar con el flujo normal (SMS)
          setSuccess('Selfie subida exitosamente');
          return true;
        }
      } else {
        setError(response.data.message || 'Error al subir selfie');
        return false;
      }
    } catch (err: any) {
      setError('Error al subir selfie: ' + (err.response?.data?.message || err.message));
      return false;
    } finally {
      setSubiendoSelfie(false);
    }
  };

  const subirDocumentos = async () => {
    try {
      setSubiendoDocumentos(true);
      setError('');

      const formData = new FormData();
      if (documentoFile) {
        formData.append('dni', documentoFile);
      }
      if (personeriaFile) {
        formData.append('personeria', personeriaFile);
      }
      // NO incluir selfie aquí, se subirá en el paso 2
      
      // Agregar datos de verificación incluyendo el token
      const verificacionDataConToken = {
        ...verificacionData,
        token: token // Agregar el token del URL
      };
      formData.append('verificacionData', JSON.stringify(verificacionDataConToken));

      await firmaApi.subirDocumentosVerificacion(formData);
      
      // En el paso 1 solo subimos el DNI, no procesamos el estado final aún
      setDocumentosSubidos(true);
      setSuccess('DNI subido exitosamente');
      
      return true;
    } catch (err: any) {
      // Si es un error de verificación duplicada, mostrar mensaje específico
      if (err.response?.data?.success === false && err.response?.data?.data?.estado === 'PENDIENTE_APROBACION') {
        setError('Ya existe una verificación pendiente para este documento. Por favor, espera la aprobación.');
        // Mostrar la verificación existente
        setVerificacionResult(err.response.data.data);
        setShowVerificacionEnviada(true);
        return false;
      }
      
      setError('Error al subir documentos: ' + (err.response?.data?.message || err.message));
      return false;
    } finally {
      setSubiendoDocumentos(false);
    }
  };

  const enviarOTP = async () => {
    try {
      setEnviandoOTP(true);
      await firmaApi.enviarOTP({ telefono: verificacionData.telefono });
      setOtpSent(true);
      setSuccess('Código OTP enviado a tu teléfono');
    } catch (err: any) {
      setError('Error al enviar OTP: ' + (err.response?.data?.message || err.message));
    } finally {
      setEnviandoOTP(false);
    }
  };

  const verificarOTP = async () => {
    try {
      setVerificandoOTP(true);
      const response = await firmaApi.verificarOTP({ 
        telefono: verificacionData.telefono, 
        codigo: otpCode,
        token: token // Enviar el token para actualizar el estado
      });
      setOtpVerified(true);
      
      // Mostrar mensaje específico para múltiples firmantes
      if (response.data.message) {
        setSuccess(response.data.message);
      } else {
        setSuccess('Código OTP verificado correctamente. El sistema verificará automáticamente cuando todos los firmantes estén listos.');
      }
      
      // Si hay múltiples firmantes, mostrar información adicional
      if (linkData && linkData.firmantes && linkData.firmantes.length > 1) {
        setTimeout(() => {
          setSuccess('Verificación completada. El sistema creará automáticamente el acuerdo cuando todos los firmantes hayan validado su identidad. Recibirás un email de Adobe Sign para firmar cuando esté listo (puedes firmar cuando quieras, sin esperar a otros).');
        }, 2000);
      }
    } catch (err: any) {
      setError('Error al verificar OTP: ' + (err.response?.data?.message || err.message));
    } finally {
      setVerificandoOTP(false);
    }
  };

  const handleSubmitVerificacion = async () => {
    try {
      setLoading(true);
      const response = await firmaApi.verificarIdentidad({
        nombre: verificacionData.nombre,
        email: verificacionData.email,
        documento: verificacionData.numeroDocumento,
        telefono: verificacionData.telefono,
        tipoDocumento: verificacionData.tipoDocumento
      });
      setVerificacionResult(response.data);
      setShowVerificacionEnviada(true);
    } catch (err: any) {
      setError('Error al enviar verificación: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFirmarDocumento = async () => {
    try {
      setLoading(true);
      console.log('🔧 Iniciando proceso de firma...');
      console.log('📋 Datos del link:', linkData);
      
      const response = await firmaApi.firmarDocumentoCliente({
        linkId: linkData?.id?.toString() || '',
        confirmacion: true
      });
      
      console.log('✅ Respuesta de firma:', response.data);
      
      if (response.data.success) {
        // Redirigir a Adobe Sign
        window.open(response.data.data.url, '_blank');
        setSuccess('Redirigiendo a Adobe Sign para firmar el documento');
      } else {
        setError(response.data.message || 'Error al preparar documento para firma');
      }
    } catch (err: any) {
      console.error('❌ Error al firmar documento:', err);
      const errorMessage = err.response?.data?.message || err.message;
      
      // Mostrar mensaje específico si no todos los firmantes están aprobados
      if (errorMessage.includes('no han validado su identidad')) {
        setError('No se puede proceder con la firma porque otros firmantes aún no han validado su identidad. Por favor, espera a que todos completen la verificación.');
      } else {
        setError('Error al firmar documento: ' + errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVolverVerificacion = () => {
    setShowVerificacionEnviada(false);
    setVerificacionResult(null);
  };

  const handleContinuarFirma = () => {
    setShowVerificacionEnviada(false);
    setActiveStep(4);
  };

  const handleCancelAgreement = async () => {
    try {
      setCancelling(true);
      const response = await firmaApi.cancelarAcuerdo(token, cancelMotivo);
      
      if (response.data.success) {
        setSuccess('Acuerdo cancelado exitosamente');
        setShowCancelDialog(false);
        setCancelMotivo('');
        // Redirigir o mostrar mensaje de confirmación
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      }
    } catch (err: any) {
      setError('Error al cancelar acuerdo: ' + (err.response?.data?.message || err.message));
    } finally {
      setCancelling(false);
    }
  };

  const openCamera = async () => {
    console.log('📷 Intentando abrir la cámara...');
    console.log('🔍 videoRef:', videoRef.current);
    console.log('🔍 photoRef:', photoRef.current);
    setCameraError(null);
    
    // Verificar que los refs estén disponibles
    if (!videoRef.current) {
      console.error('❌ videoRef no está disponible');
      setCameraError('Error: Elemento de video no disponible. Intenta recargar la página.');
      return;
    }
    
    if (!photoRef.current) {
      console.error('❌ photoRef no está disponible');
      setCameraError('Error: Elemento de canvas no disponible. Intenta recargar la página.');
      return;
    }
    
    try {
      console.log('📱 Verificando disponibilidad de mediaDevices...');
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('getUserMedia no está disponible en este navegador');
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user' // Cámara frontal
        } 
      });
      
      console.log('✅ Stream de cámara obtenido:', stream);
      console.log('🔍 Tracks del stream:', stream.getTracks());
      
      // Verificar nuevamente que videoRef esté disponible
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Esperar a que el video esté listo
        videoRef.current.onloadedmetadata = () => {
          console.log('✅ Video metadata cargado');
          setIsCameraOpen(true);
          console.log('✅ Cámara abierta correctamente');
        };
        
        videoRef.current.onerror = (error) => {
          console.error('❌ Error en el video:', error);
          setCameraError('Error al cargar el video de la cámara');
        };
        
      } else {
        console.error('❌ videoRef se perdió después de obtener el stream');
        setCameraError('Error: Elemento de video no disponible después de obtener el stream');
        // Detener el stream si no se puede usar
        stream.getTracks().forEach(track => track.stop());
      }
    } catch (err) {
      console.error('❌ Error al abrir la cámara:', err);
      setCameraError('No se pudo acceder a la cámara: ' + err.message);
    }
  };

  const closeCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      setIsCameraOpen(false);
    }
  };

  const capturePhoto = () => {
    console.log('📸 Intentando capturar foto...');
    
    if (!videoRef.current) {
      console.error('❌ videoRef no está disponible');
      setError('Error: Video no disponible');
      return;
    }
    
    if (!photoRef.current) {
      console.error('❌ photoRef no está disponible');
      setError('Error: Canvas no disponible');
      return;
    }
    
    const context = photoRef.current.getContext('2d');
    if (!context) {
      console.error('❌ No se pudo obtener el contexto del canvas');
      setError('Error: No se pudo inicializar la captura');
      return;
    }
    
    try {
      // Configurar dimensiones del canvas
      photoRef.current.width = videoRef.current.videoWidth;
      photoRef.current.height = videoRef.current.videoHeight;
      
      console.log('📐 Dimensiones del video:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
      
      // Dibujar el frame actual del video en el canvas
      context.drawImage(videoRef.current, 0, 0);
      
      // Convertir a blob
      photoRef.current.toBlob((blob) => {
        if (blob) {
          console.log('✅ Foto capturada exitosamente, tamaño:', blob.size, 'bytes');
          const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
          setSelfieFile(file);
          setSelfieCapturada(true);
          closeCamera();
          setSuccess('Selfie capturada exitosamente');
        } else {
          console.error('❌ No se pudo generar el blob de la imagen');
          setError('Error al procesar la imagen');
        }
      }, 'image/jpeg', 0.8);
      
    } catch (error) {
      console.error('❌ Error al capturar foto:', error);
      setError('Error al capturar la foto: ' + error.message);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Confirmar Datos del Firmante
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre completo"
                value={verificacionData.nombre}
                onChange={(e) => setVerificacionData({...verificacionData, nombre: e.target.value})}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={verificacionData.email}
                onChange={(e) => setVerificacionData({...verificacionData, email: e.target.value})}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                value={verificacionData.telefono}
                onChange={(e) => setVerificacionData({...verificacionData, telefono: e.target.value})}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Tipo de documento</InputLabel>
                <Select
                  value={verificacionData.tipoDocumento}
                  onChange={(e) => setVerificacionData({...verificacionData, tipoDocumento: e.target.value})}
                >
                  <MenuItem value="DNI">DNI</MenuItem>
                  <MenuItem value="PASAPORTE">Pasaporte</MenuItem>
                  <MenuItem value="CEDULA">Cédula</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de documento"
                value={verificacionData.numeroDocumento}
                onChange={(e) => setVerificacionData({...verificacionData, numeroDocumento: e.target.value})}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={verificacionData.esEmpresa}
                    onChange={(e) => setVerificacionData({...verificacionData, esEmpresa: e.target.checked})}
                  />
                }
                label="Es una empresa"
              />
            </Grid>
            {verificacionData.esEmpresa && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="CUIT (obligatorio para empresas)"
                  value={verificacionData.cuit || ''}
                  onChange={(e) => setVerificacionData({...verificacionData, cuit: e.target.value})}
                  margin="normal"
                  required={verificacionData.esEmpresa}
                  placeholder="20-12345678-9"
                  helperText="Formato: XX-XXXXXXXX-X"
                />
              </Grid>
            )}
            {verificacionData.esEmpresa && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom color="primary">
                  📄 Documento de Personería Jurídica (Obligatorio para empresas)
                </Typography>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const base64 = event.target?.result as string;
                        setVerificacionData(prev => ({
                          ...prev,
                          documentoPersoneria: base64.split(',')[1] // Remover el prefijo
                        }));
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  style={{ marginBottom: '16px' }}
                />
                <Typography variant="caption" display="block" color="textSecondary">
                  Sube el documento que acredite la personería jurídica de la empresa (PDF, JPG, PNG)
                </Typography>
              </Grid>
            )}
            {errorValidacion && (
              <Grid item xs={12}>
                <Alert severity="error">{errorValidacion}</Alert>
              </Grid>
            )}
            {clienteValidado && (
              <Grid item xs={12}>
                <Alert severity="success">
                  Datos validados correctamente
                </Alert>
              </Grid>
            )}
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Subir Documento de Identidad
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <input
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    id="documento-file"
                    type="file"
                    onChange={(e) => handleFileChange(e, 'documento')}
                  />
                  <label htmlFor="documento-file">
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      sx={{ py: 2 }}
                    >
                      Seleccionar Documento
                    </Button>
                  </label>
                  {documentoFile && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Archivo seleccionado: {documentoFile.name}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            {verificacionData.esEmpresa && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Personería Jurídica (Opcional)
                    </Typography>
                    <input
                      accept="image/*,.pdf"
                      style={{ display: 'none' }}
                      id="personeria-file"
                      type="file"
                      onChange={(e) => handleFileChange(e, 'personeria')}
                    />
                    <label htmlFor="personeria-file">
                      <Button
                        variant="outlined"
                        component="span"
                        fullWidth
                        sx={{ py: 2 }}
                      >
                        Seleccionar Personería
                      </Button>
                    </label>
                    {personeriaFile && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Archivo seleccionado: {personeriaFile.name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Capturar Selfie
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  {/* Elementos de video y canvas siempre en el DOM */}
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ 
                      width: isCameraOpen ? '100%' : '0',
                      maxWidth: '400px', 
                      border: isCameraOpen ? '1px solid #ccc' : 'none',
                      display: isCameraOpen ? 'block' : 'none'
                    }}
                  />
                  <canvas
                    ref={photoRef}
                    style={{ display: 'none' }}
                  />
                  
                  {!isCameraOpen && !selfieCapturada && (
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={openCamera}
                        startIcon={<CameraAlt />}
                        fullWidth
                        sx={{ py: 2, mb: 2 }}
                      >
                        Abrir Cámara
                      </Button>
                      
                      <Typography variant="body2" sx={{ textAlign: 'center', my: 2, color: 'text.secondary' }}>
                        o
                      </Typography>
                      
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        id="selfie-file"
                        type="file"
                        onChange={(e) => handleFileChange(e, 'selfie')}
                      />
                      <label htmlFor="selfie-file">
                        <Button
                          variant="outlined"
                          component="span"
                          fullWidth
                          sx={{ py: 2, mb: 2 }}
                        >
                          Subir Foto
                        </Button>
                      </label>
                      
                      <Button
                        variant="text"
                        onClick={() => {
                          console.log('🔍 Debug - videoRef:', videoRef.current);
                          console.log('🔍 Debug - photoRef:', photoRef.current);
                          console.log('🔍 Debug - isCameraOpen:', isCameraOpen);
                          console.log('🔍 Debug - selfieCapturada:', selfieCapturada);
                        }}
                        size="small"
                        fullWidth
                      >
                        Debug Refs
                      </Button>
                    </Box>
                  )}
                  
                  {isCameraOpen && (
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        onClick={capturePhoto}
                        startIcon={<CameraAlt />}
                      >
                        Capturar
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={closeCamera}
                      >
                        Cerrar Cámara
                      </Button>
                    </Box>
                  )}
                  
                  {selfieCapturada && (
                    <Box>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Selfie capturada exitosamente
                      </Alert>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setSelfieFile(null);
                          setSelfieCapturada(false);
                        }}
                        startIcon={<CameraAlt />}
                        fullWidth
                      >
                        Volver a Capturar
                      </Button>
                    </Box>
                  )}
                  
                  {cameraError && (
                    <Alert severity="error">
                      {cameraError}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Verificación por SMS
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  {!otpSent ? (
                    <Button
                      variant="contained"
                      onClick={enviarOTP}
                      disabled={enviandoOTP}
                      startIcon={enviandoOTP ? <CircularProgress size={20} /> : <Phone />}
                      fullWidth
                      sx={{ py: 2 }}
                    >
                      {enviandoOTP ? 'Enviando...' : 'Enviar Código OTP'}
                    </Button>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Código OTP"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value)}
                          placeholder="Ingresa el código de 6 dígitos"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          onClick={verificarOTP}
                          disabled={verificandoOTP || !otpCode}
                          startIcon={verificandoOTP ? <CircularProgress size={20} /> : <CheckCircle />}
                          fullWidth
                        >
                          {verificandoOTP ? 'Verificando...' : 'Verificar'}
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                  {otpVerified && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      Código verificado correctamente
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

             case 4:
         return (
           <Grid container spacing={3}>
             <Grid item xs={12}>
               <Typography variant="h6" gutterBottom>
                 ✅ Verificación Completada
               </Typography>
             </Grid>
             <Grid item xs={12}>
               <Card>
                 <CardContent>
                   <Box sx={{ textAlign: 'center', mb: 3 }}>
                     <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                     <Typography variant="h5" color="success.main" gutterBottom>
                       ¡Identidad Verificada Exitosamente!
                     </Typography>
                   </Box>
                   
                   <Typography variant="subtitle1" gutterBottom>
                     <strong>Documento:</strong> {linkData?.documento?.titulo || linkData?.titulo}
                   </Typography>
                   <Typography variant="body2" color="textSecondary" gutterBottom>
                     {linkData?.documento?.descripcion || linkData?.descripcion}
                   </Typography>
                   
                   <Divider sx={{ my: 3 }} />
                   
                   <Alert severity="info" sx={{ mb: 3 }}>
                     <Typography variant="body1" gutterBottom>
                       <strong>Tu verificación de identidad ha sido APROBADA automáticamente.</strong>
                     </Typography>
                     <Typography variant="body2">
                       El sistema está verificando que todos los firmantes hayan completado su verificación.
                     </Typography>
                   </Alert>
                   
                   <Box sx={{ 
                     bgcolor: 'grey.50', 
                     p: 2, 
                     borderRadius: 2, 
                     border: '1px solid',
                     borderColor: 'grey.200'
                   }}>
                     <Typography variant="body2" color="textSecondary">
                       <strong>Próximos pasos:</strong>
                     </Typography>
                     <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                       1. ✅ Verificación de identidad completada<br/>
                       2. ⏳ Esperando que otros firmantes validen su identidad<br/>
                       3. 🚀 Creación automática del acuerdo en Adobe Sign<br/>
                       4. 📧 Recibirás un email de Adobe Sign para firmar
                     </Typography>
                   </Box>
                 </CardContent>
               </Card>
             </Grid>
           </Grid>
         );

      default:
        return null;
    }
  };

  if (loading && !linkData) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <Container maxWidth="md" sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          py: 4
        }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Cargando documento...
          </Typography>
        </Container>
      </Box>
    );
  }

  if (error && !linkData) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <Container maxWidth="md" sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          py: 4
        }}>
          <Alert severity="error" sx={{ maxWidth: 600, width: '100%' }}>
            {error}
          </Alert>
        </Container>
      </Box>
    );
  }

  if (!linkData) {
    return null;
  }

  // Si se debe mostrar la vista de verificación enviada
  if (showVerificacionEnviada && verificacionResult) {
    return (
      <VerificacionEnviada
        verificacionId={verificacionResult.verificacionId}
        estado={verificacionResult.estado}
        automatica={verificacionResult.automatica}
        onfidoResult={verificacionResult.onfidoResult}
        onVolver={handleVolverVerificacion}
        onContinuar={handleContinuarFirma}
      />
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
    }}>
      {/* Header con branding */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        py: 3,
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Avatar sx={{ 
              width: 60, 
              height: 60, 
              mr: 2, 
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)'
            }}>
              <Business sx={{ fontSize: 30 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Dienst
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                Firma de Documentos
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Contenido principal */}
      <Container maxWidth="md" sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center',
        py: 4
      }}>
        {documentoYaFirmado ? (
          <Paper sx={{ 
            p: 4, 
            textAlign: 'center',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            <Typography variant="h4" gutterBottom color="success.main" sx={{ fontWeight: 700 }}>
              Documento Ya Firmado
            </Typography>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
              {linkData?.titulo}
            </Typography>
            <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
              Este documento ya ha sido firmado exitosamente.
            </Typography>
            {linkData?.fechaFirma && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Fecha de firma: {new Date(linkData.fechaFirma).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </Typography>
            )}
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => window.history.back()}
                startIcon={<ArrowBack />}
                size="large"
              >
                Volver
              </Button>
            </Box>
          </Paper>
        ) : (
          <Paper sx={{ 
            p: 4,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                Firma Electrónica
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Proceso seguro de verificación y firma de documentos
              </Typography>
            </Box>
            
            {linkData && (
              <Card sx={{ mb: 4, borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Description sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {linkData.documento?.titulo || linkData.titulo || 'Documento sin título'}
                    </Typography>
                  </Box>
                  {(linkData.documento?.descripcion || linkData.descripcion) && (
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {linkData.documento?.descripcion || linkData.descripcion}
                    </Typography>
                  )}
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={`Creado: ${linkData.documento?.created_at ? new Date(linkData.documento.created_at).toLocaleDateString() : 'Fecha no disponible'}`}
                      color="info"
                      size="small"
                      icon={<CalendarToday />}
                    />
                    <Chip 
                      label={`Expira: ${linkData.documento?.fecha_expiracion ? new Date(linkData.documento.fecha_expiracion).toLocaleDateString() : 'Fecha no disponible'}`}
                      color="warning"
                      size="small"
                      icon={<Security />}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  variant="outlined"
                  size="large"
                >
                  Atrás
                </Button>
                <Button
                  onClick={() => setShowCancelDialog(true)}
                  variant="outlined"
                  color="error"
                  size="large"
                  startIcon={<Cancel />}
                >
                  Cancelar Acuerdo
                </Button>
              </Box>
              <Box>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="outlined"
                    disabled
                    startIcon={<CheckCircle />}
                    size="large"
                    sx={{ px: 4 }}
                  >
                    Verificación Completada
                  </Button>
                ) : activeStep === 3 && otpVerified ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmitVerificacion}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
                    size="large"
                    sx={{ px: 4 }}
                  >
                    {loading ? 'Procesando...' : 'Completar Verificación'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={loading || validandoCliente || subiendoDocumentos || subiendoSelfie || enviandoOTP || verificandoOTP}
                    startIcon={
                      (validandoCliente || subiendoDocumentos || subiendoSelfie || enviandoOTP || verificandoOTP) ? 
                      <CircularProgress size={20} /> : undefined
                    }
                    size="large"
                    sx={{ px: 4 }}
                  >
                    {validandoCliente ? 'Validando...' : 
                     subiendoDocumentos ? 'Subiendo DNI...' : 
                     subiendoSelfie ? 'Subiendo Selfie...' : 
                     enviandoOTP ? 'Enviando OTP...' : 
                     verificandoOTP ? 'Verificando OTP...' : 
                     'Siguiente'}
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Container>

      <Dialog open={showSuccessDialog} onClose={() => setShowSuccessDialog(false)}>
        <DialogTitle>Verificación Enviada</DialogTitle>
        <DialogContent>
          <Typography>
            Tu verificación ha sido enviada para revisión. Te notificaremos por email cuando sea aprobada y puedas continuar con la firma del documento.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSuccessDialog(false)}>
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showCancelDialog} onClose={() => setShowCancelDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancelar Acuerdo</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Estás seguro de que deseas cancelar este acuerdo de firma?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Esta acción cancelará el proceso de firma para todos los firmantes y no se puede deshacer.
          </Typography>
          <TextField
            fullWidth
            label="Motivo de cancelación (opcional)"
            multiline
            rows={3}
            value={cancelMotivo}
            onChange={(e) => setCancelMotivo(e.target.value)}
            placeholder="Describe el motivo de la cancelación..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            No Cancelar
          </Button>
          <Button 
            onClick={handleCancelAgreement}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={20} /> : <Cancel />}
          >
            {cancelling ? 'Cancelando...' : 'Sí, Cancelar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClienteFirma; 